import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade4Zhuyin.css";

/** Helper to shuffle arrays for random quizzes */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade4Zhuyin:
 *  - Focus: More advanced reading, simple grammar usage, longer sentences
 *  - Single quiz pool (25 questions)
 */

const grammarNotes = (
  <div className="g4z-grammar-notes">
    <h4>Zhuyin for Sentence Structures & Grammar</h4>
    <p>
      In Grade 4, you’ll read more complex sentences in Zhuyin, including basic grammar points: word order, question particles, measure words, etc.
    </p>
    <ul>
      <li>
        Example measure words: ㄧ ㄓ ㄍㄡˇ (一隻狗, one dog), ㄧ ㄅㄣˇ ㄕㄨ (一本書, one book).
      </li>
      <li>
        Polite forms: ㄑㄧㄥˇ ㄒㄧㄣ ㄅㄧㄝˊ ㄔㄠˇ (請先別吵: “Please don’t make noise yet.”)
      </li>
      <li>
        More question types: ㄋㄧˇ ㄋㄚˇ ㄍㄨㄛˊ ㄖㄣˊ? (你哪國人？Which country are you from?)
      </li>
      <li>
        Continue refining tone changes for ㄅㄨˋ and ㄧ in various contexts.
      </li>
    </ul>
  </div>
);

/** 25-question pool about advanced Zhuyin grammar & usage */
const zhuyinQuestions = [
  {
    question: "Which measure word is often seen as ㄓ for animals (e.g., dogs)?",
    correctAnswer: "ㄓ (隻) used for many animals",
    options: ["ㄅㄣ (本)", "ㄓ (隻)", "ㄓˋ (志)"]
  },
  {
    question: "Asking 'Where are you from?' => 你哪國人？In Zhuyin could be?",
    correctAnswer: "ㄋㄧˇ ㄋㄚˇ ㄍㄨㄛˊ ㄖㄣˊ",
    options: [
      "ㄋㄧˇ ㄏㄞˇ ㄎㄨㄛˋ ㄖㄣˊ",
      "ㄋㄧˇ ㄋㄚˇ ㄍㄨㄛˊ ㄖㄣˊ",
      "ㄋㄧˇ ㄇㄟˋ ㄍㄨㄛˊ ㄧˇ"
    ]
  },
  {
    question: "Tone changes: '一' (ㄧ) sometimes goes 2nd tone before 4th tone. E.g., ㄧˊ ㄅㄣˇ if followed by 4th tone. This rule is for?",
    correctAnswer: "Avoiding repeated strong tones, making speech flow",
    options: [
      "No reason, random",
      "Only for writing, not speech",
      "Smoother speech to avoid two 4th tones in a row"
    ]
  },
  {
    question: "Which measure word is ㄅㄣˇ used for?",
    correctAnswer: "Books (書), magazines, etc.",
    options: [
      "Small animals",
      "Books or magazines",
      "Vehicles"
    ]
  },
  {
    question: "Asking someone politely not to do something: ㄑㄧㄥˇ ㄅㄧㄝˊ ㄗㄠˋ ㄔㄠˇ => 'Please don’t make noise.' The ㄅㄧㄝˊ indicates?",
    correctAnswer: "'Don’t do (something)' in a polite form",
    options: [
      "'Stop or else!' strongly",
      "'I want more noise now.'",
      "Polite negative imperative: 'don’t'"
    ]
  },
  {
    question: "Saying 'I only have one pen' => ㄨㄛˇ ㄓˊ ㄧˋ ㄓ ㄅㄧˇ => might be spelled with tone changes. Why?",
    correctAnswer: "Because ㄧˋ (4th) might shift if next tone is also 4th. Context can apply rule",
    options: [
      "No shift needed; always 4th tone",
      "Zhuyin can’t handle that phrase",
      "We shift tone for ㄧ if next word is 4th"
    ]
  },
  {
    question: "To express 'You must study first' => ㄋㄧˇ ㄏㄨㄚˋ ㄏㄠˇ ㄒㄧㄢ ㄒㄩㄝˊ ㄒㄧˊ => (你要先學習). The word ㄏㄠˇ means?",
    correctAnswer: "Must or a strong suggestion in daily usage, though literal is 'good'—context-based",
    options: [
      "No meaning at all",
      "Always 'have to' in grammar sense",
      "A strong suggestion or 'must' depending on context"
    ]
  },
  {
    question: "If you see ㄕˊ ㄐㄧㄢ (時間), it means 'time.' If you want to say 'I have no time,' => ㄨㄛˇ ㄇㄟˊ ㄧㄡˇ ㄕˊ ㄐㄧㄢ?",
    correctAnswer: "Yes, spelled ㄨㄛˇ ㄇㄟˊ ㄧㄡˇ ㄕˊ ㄐㄧㄢ with correct tones",
    options: [
      "We use pinyin only for time",
      "That’s incorrect usage in Zhuyin",
      "Yes, it’s correct for 'I have no time'"
    ]
  },
  {
    question: "Which question particle might appear at the end for yes/no questions, like ㄇㄚ˙?",
    correctAnswer: "ㄇㄚ˙ = 'ma' (嗎 in characters)",
    options: [
      "ㄇㄜ (么)",
      "ㄇㄚˇ always 3rd tone",
      "ㄇㄚ˙ neutral tone"
    ]
  },
  {
    question: "A request: ㄋㄧˇ ㄎㄜˇ ㄧˇ ㄅㄤ ㄨㄛˇ ㄇㄚ˙ => 'Can you help me?' If you add ㄒㄧㄢ => 'ㄑㄧㄥˇ ㄒㄧㄢ ㄅㄤ ㄨㄛˇ' means?",
    correctAnswer: "'Please help me first.'",
    options: [
      "Asking for directions only",
      "No difference in meaning",
      "'Please help me first.'"
    ]
  },
  {
    question: "In grammar, using ㄉㄜ˙ => 'de' (的) to connect. E.g., ㄨㄛˇ ㄉㄜ˙ ㄅㄟˇ ㄅㄞˋ => means 'my cup,' etc. This shows?",
    correctAnswer: "Possessive or descriptive linking word in Chinese",
    options: [
      "Always a verb marker",
      "No real function",
      "A possessive or descriptive marker"
    ]
  },
  {
    question: "Asking 'Which day do we start class?' => ㄨㄛˇ ㄇㄣ ㄕㄜˊ ㄇㄜ˙ ㄕˊ ㄏㄡˋ ㄕㄧ ㄎㄝˋ => daily usage might need measure word for days. True or false?",
    correctAnswer: "True, measure words or time words help specify days/hours",
    options: [
      "False, no measure word needed ever",
      "True, we often specify time or day measure words",
      "We only use pinyin"
    ]
  },
  {
    question: "If you see ㄊㄧㄥ ㄂ㄨˋ ㄉㄜˇ ㄉㄨㄛ => 'tīng bù de duō,' might be incomplete. Possibly means?",
    correctAnswer: "He/she can’t listen (or won’t understand) much. Need context",
    options: [
      "Means 'I have lots of homework'",
      "A direct phrase for 'I love noodles'",
      "In context, implies 'not hearing/understanding much'"
    ]
  },
  {
    question: "Saying '先...' => ㄒㄧㄢ (1st tone?), means 'first, do something.' A typical phrase: ㄒㄧㄢ ㄏㄜ ㄕㄨㄟˇ => 'Drink water first.' This is an example of?",
    correctAnswer: "Sequence words in grammar usage (先..., 然後...)",
    options: [
      "Polite refusal phrase",
      "Tense marker for past",
      "Sequence word usage: first do X, then Y"
    ]
  },
  {
    question: "In daily grammar, ㄨㄛˇ ㄗㄞˋ ㄒㄩㄝˊ ㄓㄨˋ ㄧㄣ => 'I’m learning Zhuyin.' The ㄗㄞˋ implies?",
    correctAnswer: "Progressive action or 'currently doing'",
    options: [
      "Past tense marker",
      "Future tense only",
      "Progressive, 'I’m currently learning'"
    ]
  },
  {
    question: "Measure word for books: ㄅㄣˇ (本), for pens: ㄓ (枝), for people: ㄍㄜ˙ (個). This demonstrates?",
    correctAnswer: "Chinese uses different measure words for different nouns",
    options: [
      "We only have one measure word",
      "Zhuyin doesn’t show measure words",
      "Different measure words for different nouns"
    ]
  },
  {
    question: "‘你先别走’ => ㄋㄧˇ ㄒㄧㄢ ㄅㄧㄝˊ ㄗㄡˇ => means?",
    correctAnswer: "Don’t leave yet, or 'Please wait before leaving.'",
    options: [
      "Hurry up and leave now",
      "No real meaning",
      "Don’t leave yet"
    ]
  },
  {
    question: "If you see ㄨㄛˇ ㄒㄧㄤ ㄐㄧㄠ ㄋㄧˇ ㄉㄜ˙ ㄆㄥˊ ㄧㄡˇ => could mean 'I want to meet your friend.' The ㄐㄧㄠ often means?",
    correctAnswer: "To meet or introduce (介紹, jièshào). Tones matter though",
    options: [
      "No usage for meeting",
      "Always means 'to buy' something",
      "Likely 'introduce' or 'to meet'"
    ]
  },
  {
    question: "To say 'I’m very busy now, so I can’t help you' => ㄨㄛˇ ㄒㄧㄢˋ ㄗㄞˋ ㄏㄣˇ ㄇㄤˊ, ㄙㄨㄛˇ ㄧˇ ㄅㄨˋ ㄋㄥˊ ㄅㄤ ㄋㄧˇ => shows grammar linking?",
    correctAnswer: "'Therefore' linking with ㄙㄨㄛˇ ㄧˇ and negative ability ㄅㄨˋ ㄋㄥˊ",
    options: [
      "No grammar used",
      "Yes, 'so' linking cause/effect + negative ability",
      "Asking a question about time"
    ]
  },
  {
    question: "Asking about nationality: ㄋㄧˇ ㄕˋ ㄑㄧㄥ ㄏㄨㄚˋ ㄖㄣˊ ㄇㄚ˙ => maybe nonsense if tone is off. Usually ㄋㄧˇ ㄕˋ ㄋㄚˇ ㄍㄨㄛˊ ㄖㄣˊ?",
    correctAnswer: "Yes, tone/character usage for '哪國' is crucial for 'Which country?'",
    options: [
      "No difference at all",
      "Zhuyin can’t show '哪國'",
      "Yes, tone/character usage matters for correct question"
    ]
  },
  {
    question: "Writing 'Thank you for your help' => ㄒㄧㄝˋ ㄒㄧㄝ˙ ㄋㄧˇ ㄉㄜ˙ ㄅㄤ ㄓㄨˋ => means?",
    correctAnswer: "'Thanks for your help.'",
    options: [
      "Please help me again",
      "No meaning in Chinese grammar",
      "'Thanks for your help.'"
    ]
  },
  {
    question: "‘要不要...?’ => ㄧㄠˋ ㄅㄨˋ ㄧㄠˋ can form 'Do you want ... or not?' question. E.g., ㄧㄠˋ ㄅㄨˋ ㄧㄠˋ ㄏㄜ ㄕㄨㄟˇ => 'Want water or not?' This is a grammar pattern for?",
    correctAnswer: "Yes/no question pattern by repeating the verb or main phrase",
    options: [
      "No common usage",
      "An advanced classical form only",
      "A typical yes/no question pattern in Mandarin"
    ]
  }
];

export default function Grade4Zhuyin() {
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min

  // Timer effect
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Quick quiz generator
  const generateQuiz = () => {
    const q = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // Quick quiz handle
  const handleAnswerClick = (ans) => {
    setSelectedAnswer(ans);
    if (quiz && ans === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Start Final Quiz => same 25 Q
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  // Final quiz answer
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g4chi-page">
      <h1 className="g4chi-title">Grade 4 Zhuyin Lessons</h1>
      <p className="g4chi-description">
        Focus on sentence structures, grammar, measure words, and more advanced usage of Zhuyin.
      </p>

      {/* Lesson Content */}
      <div className="g4chi-lesson-content">
        <h2 className="g4chi-lesson-title">注音 (Grammar & Sentence Structures)</h2>
        <p className="g4chi-lesson-description">
          Learn how to form more complex sentences in Zhuyin, including measure words, polite requests, and question particles.
        </p>
        <p className="g4chi-lesson-example">
          <strong>Example:</strong> ㄨㄛˇ ㄧㄡˇ ㄧˋ ㄓ ㄍㄡˇ (I have one dog).
        </p>
        {grammarNotes}
      </div>

      {/* Quiz Section */}
      <div className="g4chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g4chi-quick-quiz-container">
          <h2 className="g4chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g4chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g4chi-quiz-question">{quiz.question}</p>
              <div className="g4chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g4chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g4chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g4chi-final-quiz-container">
          <h2 className="g4chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g4chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && (
            <div className="g4chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>Question {finalQuizIndex + 1} of {finalQuizQuestions.length}</p>
              <p className="g4chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g4chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g4chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
